import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Badge } from './ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from './ui/table';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell, LineChart, Line } from 'recharts';
import { Download, Calendar, TrendingUp, Users, Clock, DollarSign } from 'lucide-react';
import { useState } from 'react';

export function Reports() {
  const [selectedPeriod, setSelectedPeriod] = useState('month');

  // Mock data for charts
  const monthlyRevenue = [
    { month: 'Jan', revenue: 684750, hours: 890 },
    { month: 'Feb', revenue: 726000, hours: 920 },
    { month: 'Mar', revenue: 775500, hours: 980 },
    { month: 'Apr', revenue: 841500, hours: 1020 },
    { month: 'May', revenue: 924000, hours: 1150 },
    { month: 'Jun', revenue: 1001000, hours: 1240 },
  ];

  const packageDistribution = [
    { name: 'Monthly Premium', value: 35, color: '#8884d8' },
    { name: 'Monthly Regular', value: 28, color: '#82ca9d' },
    { name: '30-hour package', value: 20, color: '#ffc658' },
    { name: '10-hour package', value: 12, color: '#ff7c7c' },
    { name: 'Daily Pass', value: 5, color: '#8dd1e1' },
  ];

  const weeklyUsage = [
    { day: 'Mon', hours: 45 },
    { day: 'Tue', hours: 52 },
    { day: 'Wed', hours: 48 },
    { day: 'Thu', hours: 61 },
    { day: 'Fri', hours: 55 },
    { day: 'Sat', hours: 28 },
    { day: 'Sun', hours: 15 },
  ];

  const topClients = [
    { name: 'John Doe', hoursUsed: 120, revenue: 16445, package: 'Monthly Premium' },
    { name: 'Sarah Wilson', hoursUsed: 85, revenue: 21945, package: '30-hour package' },
    { name: 'Mike Johnson', hoursUsed: 78, revenue: 16445, package: 'Monthly Premium' },
    { name: 'Emma Brown', hoursUsed: 45, revenue: 8195, package: '10-hour package' },
    { name: 'Alex Turner', hoursUsed: 32, revenue: 10945, package: 'Monthly Regular' },
  ];

  const expiringSubscriptions = [
    { client: 'Alex Turner', subscription: 'Monthly Regular', expires: '2024-06-25', hoursLeft: 12 },
    { client: 'Lisa Chen', subscription: '30-hour package', expires: '2024-06-28', hoursLeft: 5 },
    { client: 'David Park', subscription: '10-hour package', expires: '2024-07-02', hoursLeft: 3 },
    { client: 'Jennifer Lee', subscription: 'Monthly Premium', expires: '2024-07-05', hoursLeft: 25 },
  ];

  const getPackageColor = (packageName: string) => {
    switch (packageName) {
      case 'Monthly Premium':
        return 'bg-purple-100 text-purple-800';
      case 'Monthly Regular':
        return 'bg-blue-100 text-blue-800';
      case '30-hour package':
        return 'bg-orange-100 text-orange-800';
      case '10-hour package':
        return 'bg-green-100 text-green-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const getDaysUntilExpiry = (expiryDate: string) => {
    const today = new Date();
    const expiry = new Date(expiryDate);
    const timeDiff = expiry.getTime() - today.getTime();
    const daysDiff = Math.ceil(timeDiff / (1000 * 3600 * 24));
    return daysDiff;
  };

  // Custom tooltip formatter for charts
  const formatTooltip = (value: any, name: string) => {
    if (name === 'revenue') {
      return [`₱${value.toLocaleString()}`, 'Revenue'];
    }
    return [value, name];
  };

  return (
    <div className="space-y-6">
      {/* Header with filters */}
      <div className="flex justify-between items-center">
        <div>
          <h2>Reports &amp; Analytics</h2>
          <p className="text-muted-foreground">Comprehensive insights into your coworking space</p>
        </div>
        <div className="flex gap-2">
          <Select value={selectedPeriod} onValueChange={setSelectedPeriod}>
            <SelectTrigger className="w-32">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="week">This Week</SelectItem>
              <SelectItem value="month">This Month</SelectItem>
              <SelectItem value="quarter">This Quarter</SelectItem>
              <SelectItem value="year">This Year</SelectItem>
            </SelectContent>
          </Select>
          <Button variant="outline">
            <Download className="mr-2 h-4 w-4" />
            Export
          </Button>
        </div>
      </div>

      {/* Key Metrics */}
      <div className="grid gap-4 md:grid-cols-4">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm">Total Revenue</CardTitle>
            <DollarSign className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl">₱1,001,000</div>
            <p className="text-xs text-muted-foreground">
              <span className="text-green-600">+8.3%</span> from last month
            </p>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm">Hours Utilized</CardTitle>
            <Clock className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl">1,240</div>
            <p className="text-xs text-muted-foreground">
              <span className="text-green-600">+12.5%</span> from last month
            </p>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm">Active Members</CardTitle>
            <Users className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl">89</div>
            <p className="text-xs text-muted-foreground">
              <span className="text-green-600">+5.2%</span> from last month
            </p>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm">Occupancy Rate</CardTitle>
            <TrendingUp className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl">78%</div>
            <p className="text-xs text-muted-foreground">
              <span className="text-green-600">+2.1%</span> from last month
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Charts */}
      <div className="grid gap-6 md:grid-cols-2">
        {/* Revenue Trend */}
        <Card>
          <CardHeader>
            <CardTitle>Revenue Trend</CardTitle>
            <CardDescription>Monthly revenue and hours utilization</CardDescription>
          </CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={300}>
              <LineChart data={monthlyRevenue}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="month" />
                <YAxis />
                <Tooltip formatter={formatTooltip} />
                <Line type="monotone" dataKey="revenue" stroke="#8884d8" name="revenue" />
                <Line type="monotone" dataKey="hours" stroke="#82ca9d" name="Hours" />
              </LineChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>

        {/* Package Distribution */}
        <Card>
          <CardHeader>
            <CardTitle>Package Distribution</CardTitle>
            <CardDescription>Popular package types by usage</CardDescription>
          </CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={300}>
              <PieChart>
                <Pie
                  data={packageDistribution}
                  cx="50%"
                  cy="50%"
                  outerRadius={100}
                  dataKey="value"
                  label={({ name, percent }) => `${name}: ${(percent * 100).toFixed(0)}%`}
                >
                  {packageDistribution.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={entry.color} />
                  ))}
                </Pie>
                <Tooltip />
              </PieChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>

        {/* Weekly Usage Pattern */}
        <Card>
          <CardHeader>
            <CardTitle>Weekly Usage Pattern</CardTitle>
            <CardDescription>Hours used by day of the week</CardDescription>
          </CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={weeklyUsage}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="day" />
                <YAxis />
                <Tooltip />
                <Bar dataKey="hours" fill="#8884d8" />
              </BarChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>

        {/* Top Clients */}
        <Card>
          <CardHeader>
            <CardTitle>Top Clients</CardTitle>
            <CardDescription>Highest usage and revenue contributors</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {topClients.map((client, index) => (
                <div key={index} className="flex items-center justify-between">
                  <div className="space-y-1">
                    <p className="font-medium">{client.name}</p>
                    <div className="flex items-center gap-2">
                      <Badge className={getPackageColor(client.package)}>
                        {client.package}
                      </Badge>
                      <span className="text-sm text-muted-foreground">
                        {client.hoursUsed}h used
                      </span>
                    </div>
                  </div>
                  <div className="text-right">
                    <p className="font-medium">₱{client.revenue.toLocaleString()}</p>
                    <p className="text-sm text-muted-foreground">revenue</p>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Renewal Alerts */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Calendar className="h-4 w-4 text-orange-600" />
            Renewal Alerts
          </CardTitle>
          <CardDescription>Subscriptions expiring soon</CardDescription>
        </CardHeader>
        <CardContent>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Client</TableHead>
                <TableHead>Subscription</TableHead>
                <TableHead>Expires</TableHead>
                <TableHead>Days Left</TableHead>
                <TableHead>Hours Remaining</TableHead>
                <TableHead>Action</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {expiringSubscriptions.map((sub, index) => {
                const daysLeft = getDaysUntilExpiry(sub.expires);
                return (
                  <TableRow key={index}>
                    <TableCell>{sub.client}</TableCell>
                    <TableCell>
                      <Badge className={getPackageColor(sub.subscription)}>
                        {sub.subscription}
                      </Badge>
                    </TableCell>
                    <TableCell>{sub.expires}</TableCell>
                    <TableCell>
                      <span className={daysLeft <= 3 ? 'text-red-600' : 'text-orange-600'}>
                        {daysLeft} days
                      </span>
                    </TableCell>
                    <TableCell>{sub.hoursLeft}</TableCell>
                    <TableCell>
                      <Button variant="outline" size="sm">
                        Contact
                      </Button>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </div>
  );
}